/**
 * Which pieces of the in-room overlay a player wants to see, remembered between runs.
 *
 * The player report this exists for: "the top 10, your run, the map and show human/bot names —
 * they should all be options that can be shown/hidden and that setting should be stored in
 * localstorage so it stays (same with the buttons in case the user doesn't use them)". So this
 * is a *preference* store, not a game one: nothing here is on the wire, nothing here is per
 * room, and every default is exactly what the HUD drew before this card.
 *
 * It is a module-level singleton, unlike `HudStore` — which is per-mount because it holds one
 * connection's run. A preference belongs to the person rather than to the arena they are
 * currently in, and it has two readers that never meet: React (through `useHudSettings`) and the
 * Phaser scene, which reads `getSnapshot()` on the frame it draws a name label. Handing it
 * through props would mean plumbing a value into the render loop, which is the one thing the
 * React/Phaser boundary exists to avoid.
 *
 * `useSyncExternalStore`'s contract is why `getSnapshot` returns the same object until something
 * actually changes: a store that rebuilt its snapshot per read would re-render the HUD forever.
 *
 * **Storage is never trusted and never allowed to throw.** A private window, blocked site data
 * and a hand-edited value are all the same thing to this file — the defaults. Reading
 * `localStorage` at all throws in some browsers when cookies are blocked, so even the *access*
 * is inside a try.
 */

/**
 * The key, with the shape's version in it.
 *
 * A change to the shape bumps the suffix rather than migrating: what is stored is thirteen
 * booleans about what a HUD draws and what it costs to draw, and a player whose preferences are
 * read as "the defaults" once has lost nothing worth writing a migration for. The old key is
 * simply never read again — v1 is the nine overlay switches, v2 added `batterySaver`, v3 added
 * `perfStats`, v4 added `notices`, and v5 adds `joystick`.
 */
export const HUD_SETTINGS_STORAGE_KEY = 'chompdome-hud-settings-v5';

/** Everything a player may turn off, and nothing that is not an overlay. */
export interface HudSettings {
  /** The top-ten board (top five in portrait — the cut is CSS and this does not change it). */
  leaderboard: boolean;
  /** "Your run": mass, rank, dots, time, kills. */
  stats: boolean;
  minimap: boolean;
  /** The human/bot split, top left. */
  population: boolean;
  /** Name labels over other people's blobs. */
  humanNames: boolean;
  /** Name labels over bots, which are the ones prefixed `BOT`. */
  botNames: boolean;
  /**
   * The on-screen split / eject / power-up controls in the bottom-right corner. Turning them off
   * is what a player who uses the keyboard asked for; it takes nothing away from a keyboard, and
   * neither Leave nor the gear ever goes with them.
   */
  actionButtons: boolean;
  /**
   * The key bindings along the bottom of the arena.
   *
   * Permanent on a roomy screen and CONTROL_HINT_MS of each life on a compact one — see
   * `ControlHint`. **The ✕ on the panel itself writes this key**, which is why there is no
   * `dismissed` flag beside it: a second piece of state would be one the settings dialog could
   * not see, so a player who dismissed the hint and then toggled the switch back on would find
   * the switch on and the panel gone.
   */
  controlHint: boolean;
  /**
   * The thumb stick on the right-hand side of the arena, on a touch surface.
   *
   * On by default, and that is `notices`' argument rather than `batterySaver`'s: a player asked
   * for this control, so "the defaults" has to be the control being there. The switch is the
   * other half of the same sentence — some players steer by dragging the arena and will want the
   * middle of their screen back — and turning it off takes nothing away, because drag-to-steer
   * is untouched either way. It is read only where the on-screen controls are drawn at all
   * (`useTouchControls`): a desktop has a mouse and a keyboard and never sees it.
   */
  joystick: boolean;
  /**
   * The recent-sayings overlay: the short-lived lines of what people in the room have said.
   *
   * It hides the *overlay*, never the control: the picker button stays, because a player who
   * cleaned up their screen did not ask to be stopped from speaking. The population badge's panel
   * is under the opposite rule (hiding the badge hides the way into the roster) and the two differ
   * for the reason the name toggles do — this is a panel drawn over the map, that was a detail
   * behind a control.
   */
  sayings: boolean;
  /**
   * The event feed, upper centre: who collected what, who hit whom, who was eaten.
   *
   * `sayings`' rule and not the population badge's — it hides a panel drawn over the map, and
   * there is no control behind it to go with it. On by default, which is where it differs from
   * the two switches under it: this is a feature a player asked to be *shown*, so the default is
   * the feature working, and the switch is the "in case a user doesn't want to see them" half of
   * the same report.
   */
  notices: boolean;
  /**
   * Draw the arena more cheaply, for longer play on a phone.
   *
   * **The only setting in here that is off by default**, and the only one that is not about what
   * the overlay draws — it is read by `../render-profile.ts` for the frame cap and by
   * `ArenaScene` on the frame, for the decorations. Off is today's behaviour, exactly as every
   * other default in this record is.
   *
   * **IT MAY CHANGE FIDELITY AND NEVER VISIBILITY.** A player in battery saver still sees every
   * pellet, every orb, every projectile, every bot mark and every flag: what goes is the frame
   * rate, the minor grid pass, the pellet pop-in ramp and the purely decorative animation on
   * things that are still drawn. Anything else would be a switch a player could configure
   * themselves into an advantage — or out of one — with.
   */
  batterySaver: boolean;
  /**
   * The frame-rate readout, off by default.
   *
   * **It is a diagnostic and it says so.** A battery figure is measured with a phone and a
   * clock; what nobody could see from here is what the device was actually *achieving* while it
   * spent that battery — and the difference between "the cap is 30 and it draws 30" and "the cap
   * is 30 and it draws 14" is the difference between a game that is capped and a game that is
   * saturated. Those two want opposite fixes, and guessing which one a phone was in is what cost
   * this feature two rounds of measurement.
   *
   * Drawn as one small line, over the arena, out of the way of everything a player uses.
   */
  perfStats: boolean;
}

export type HudSettingKey = keyof HudSettings;

/** Today's behaviour, which is what an absent, corrupt or partial stored value reads as. */
export const HUD_SETTINGS_DEFAULTS: HudSettings = {
  leaderboard: true,
  stats: true,
  minimap: true,
  population: true,
  humanNames: true,
  botNames: true,
  actionButtons: true,
  controlHint: true,
  // On: the player asked for the stick, so the default is the stick being there. See above.
  joystick: true,
  sayings: true,
  // On: the player asked for these messages, so "the defaults" has to be them being drawn.
  notices: true,
  // Off, like every default in here: today's behaviour for a player who never opens the modal.
  batterySaver: false,
  // A diagnostic, so also off: nobody is shown a frame counter they did not ask for.
  perfStats: false,
};

const KEYS = Object.keys(HUD_SETTINGS_DEFAULTS) as HudSettingKey[];

/** As much of `Storage` as this file uses. Injectable so a test can hand it one that throws. */
export interface HudSettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The browser's own, or null where there is not one.
 *
 * Reading the property is inside the try because that is where some browsers throw when site
 * data is blocked — a `typeof` guard alone would not catch it.
 */
export function browserStorage(): HudSettingsStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * What was stored, field by field, with anything that is not a boolean replaced by its default.
 *
 * Per field rather than all-or-nothing: a value written by an older build knows about fewer
 * toggles than this one, and reading that as "the defaults" would silently undo the settings a
 * player did choose.
 */
export function parseHudSettings(raw: string | null): HudSettings {
  if (raw === null) return { ...HUD_SETTINGS_DEFAULTS };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...HUD_SETTINGS_DEFAULTS };
  }
  if (typeof parsed !== 'object' || parsed === null) return { ...HUD_SETTINGS_DEFAULTS };

  const stored = parsed as Record<string, unknown>;
  const settings = { ...HUD_SETTINGS_DEFAULTS };
  for (const key of KEYS) {
    const value = stored[key];
    if (typeof value === 'boolean') settings[key] = value;
  }
  return settings;
}

export class HudSettingsStore {
  private snapshot: HudSettings;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage: HudSettingsStorage | null = browserStorage()) {
    this.snapshot = this.read();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Stable between changes, which is `useSyncExternalStore`'s whole requirement. */
  getSnapshot = (): HudSettings => this.snapshot;

  set(key: HudSettingKey, value: boolean): void {
    if (this.snapshot[key] === value) return;
    this.snapshot = { ...this.snapshot, [key]: value };
    this.write();
    for (const listener of this.listeners) listener();
  }

  toggle(key: HudSettingKey): void {
    this.set(key, !this.snapshot[key]);
  }

  /**
   * Back to the defaults — every overlay on, battery saver and the frame readout off. Also how a
   * test puts the singleton back where it found it.
   */
  reset(): void {
    this.snapshot = { ...HUD_SETTINGS_DEFAULTS };
    this.write();
    for (const listener of this.listeners) listener();
  }

  private read(): HudSettings {
    try {
      return parseHudSettings(this.storage?.getItem(HUD_SETTINGS_STORAGE_KEY) ?? null);
    } catch {
      // A storage that throws on read is a storage this HUD does not have. Draw everything.
      return { ...HUD_SETTINGS_DEFAULTS };
    }
  }

  private write(): void {
    try {
      this.storage?.setItem(HUD_SETTINGS_STORAGE_KEY, JSON.stringify(this.snapshot));
    } catch {
      // Quota, a private window, blocked site data: the setting still applies to this session.
    }
  }
}

/** The one every reader shares — see the module comment for why this is a singleton. */
export const hudSettings = new HudSettingsStore();
